import { useUnit } from 'effector-react';
import { Trash2 } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { AccountWithWalletsAndPermissions } from 'shared/api';
import {
	ConfirmModalProps,
	createConfirmModal,
	ModalFormActionIconProps,
	ModalFormButtonProps,
} from 'shared/ui';
import { DeleteAccountModel } from '../../model';

export const createDeleteAccountModal = (model: DeleteAccountModel) => {
	const ConfirmModal = createConfirmModal(model);

	const DeleteAccountModal = forwardRef<HTMLDivElement, ConfirmModalProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [account] = useUnit([model.$modalData]);

		return (
			<ConfirmModal
				ref={ref}
				title={t('delete-account.title')}
				cancelButtonProps={{
					children: t('delete-account.cancel'),
				}}
				submitButtonProps={{
					children: t('delete-account.delete'),
					variant: 'destructive',
				}}
				{...props}
			>
				{t('delete-account.description', { name: account?.name })}
			</ConfirmModal>
		);
	});

	const DeleteAccountModalButton = forwardRef<
		HTMLButtonElement,
		ModalFormButtonProps<AccountWithWalletsAndPermissions>
	>(({ modalData, ...props }, ref) => {
		const { t } = useTranslation();

		const [viewerAccountId, canCreateSubAccounts] = useUnit([
			viewerModel.$accountId,
			viewerModel.$canCreateSubAccounts,
		]);

		return (
			<ConfirmModal.Button
				ref={ref}
				data-test-id='delete-account-button'
				variant='destructive'
				leftSection={<Trash2 />}
				disabled={viewerAccountId === modalData.id || !canCreateSubAccounts}
				modalData={modalData}
				{...props}
			>
				{t('delete-account.delete')}
			</ConfirmModal.Button>
		);
	});

	const DeleteAccountModalActionIcon = forwardRef<
		HTMLButtonElement,
		ModalFormActionIconProps<AccountWithWalletsAndPermissions>
	>(({ modalData, ...props }, ref) => {
		const [viewerAccountId, canCreateSubAccounts] = useUnit([
			viewerModel.$accountId,
			viewerModel.$canCreateSubAccounts,
		]);

		return (
			<ConfirmModal.ActionIcon
				ref={ref}
				data-test-id='delete-account-action-icon'
				variant='ghost-destructive'
				disabled={viewerAccountId === modalData.id || !canCreateSubAccounts}
				modalData={modalData}
				{...props}
			>
				<Trash2 />
			</ConfirmModal.ActionIcon>
		);
	});

	return Object.assign(DeleteAccountModal, {
		Button: DeleteAccountModalButton,
		ActionIcon: DeleteAccountModalActionIcon,
	});
};
